import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Crown, Users, User, Mail, Power, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Badge } from '../../../../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../../components/ui/select';
import { toast } from 'sonner';
import { adminService } from '../../../../services/adminService';
import { AnnouncementContactsCard } from '../../../../components/AnnouncementContactsCard';

const ROLE_META = {
  owner: { label: 'Owner', icon: Crown, badge: 'bg-amber-100 text-amber-800' },
  manager: { label: 'Manager', icon: Users, badge: 'bg-blue-100 text-blue-800' },
  member: { label: 'Member', icon: User, badge: 'bg-gray-100 text-gray-800' },
};
const STATUS_BADGE = {
  invited: 'bg-amber-100 text-amber-700',
  active: 'bg-green-100 text-green-700',
  disabled: 'bg-gray-200 text-gray-600',
};
const ROLES = ['owner', 'manager', 'member'];

const timeAgo = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const sec = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  return `${mo}mo ago`;
};

const RoleIcon = ({ role, className }) => {
  const Icon = (ROLE_META[role] || ROLE_META.member).icon;
  return <Icon className={className} />;
};

export const UsersTab = ({ clientId }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [addData, setAddData] = useState({ email: '', name: '', account_role: 'member' });
  const [inviteLink, setInviteLink] = useState(null);

  const announcementApi = useMemo(() => ({
    list: () => adminService.getClientAnnouncementContacts(clientId),
    add: (payload) => adminService.addClientAnnouncementContact(clientId, payload),
    update: (id, payload) => adminService.updateClientAnnouncementContact(clientId, id, payload),
    remove: (id) => adminService.deleteClientAnnouncementContact(clientId, id),
  }), [clientId]);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      setUsers(await adminService.getClientUsers(clientId));
    } catch {
      toast.error('Failed to load account users');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!addData.email || !addData.name) {
      toast.error('Email and name are required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await adminService.addClientUser(clientId, addData);
      setShowAdd(false);
      setAddData({ email: '', name: '', account_role: 'member' });
      load();
      if (res?.email_sent === false && res?.invite_link) {
        setInviteLink(res.invite_link);
        toast.warning(res.message || 'User added, but the invite email could not be sent.');
      } else {
        toast.success(res?.message || `Invitation sent to ${addData.email}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRole = async (membershipId, role) => {
    try {
      await adminService.updateClientUserRole(clientId, membershipId, role);
      toast.success('Role updated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update role');
      load();
    }
  };

  const handleStatus = async (u) => {
    const next = u.status === 'disabled' ? 'active' : 'disabled';
    try {
      await adminService.setClientUserStatus(clientId, u.id, next);
      toast.success(`User ${next === 'active' ? 'enabled' : 'disabled'}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleResend = async (u) => {
    try {
      const res = await adminService.resendClientUserInvite(clientId, u.id);
      if (res?.email_sent === false && res?.invite_link) {
        setInviteLink(res.invite_link);
        toast.warning(res.message || 'Could not send the email. Share the link instead.');
      } else {
        toast.success(res?.message || `Invite re-sent to ${u.email}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resend');
    }
  };

  const handleRemove = async (u) => {
    if (!window.confirm(`Remove ${u.name} from this account? Their login will be deleted.`)) return;
    try {
      await adminService.removeClientUser(clientId, u.id);
      toast.success('User removed');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove');
    }
  };

  return (
    <div className="space-y-4 mt-4" data-testid="admin-client-users-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">All logins attached to this client account.</p>
        <Button size="sm" className="bg-amber-500 hover:bg-amber-600" onClick={() => setShowAdd(true)} data-testid="admin-add-client-user-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Login
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-amber-500"></div>
        </div>
      ) : (
        <div className="border rounded-lg divide-y">
          {users.map((u) => (
            <div key={u.id} className="flex items-center justify-between p-3" data-testid={`admin-client-user-${u.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                  <RoleIcon role={u.account_role} className="h-4 w-4 text-gray-700" />
                </div>
                <div className="min-w-0">
                  <div className="font-medium truncate">{u.name}</div>
                  <div className="text-sm text-gray-500 truncate">{u.email}</div>
                  {u.status === 'invited' && (
                    <div className="text-xs text-amber-600 mt-0.5 flex items-center gap-1" data-testid={`admin-invite-sent-${u.id}`}>
                      <Mail className="h-3 w-3" />
                      Invite sent {timeAgo(u.invite_sent_at) || 'recently'}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge className={`${STATUS_BADGE[u.status] || STATUS_BADGE.active} text-xs`}>
                  {u.status === 'invited' && <Mail className="h-3 w-3 mr-1" />}
                  <span className="capitalize">{u.status === 'invited' ? 'Invitation pending' : u.status}</span>
                </Badge>
                <Select value={u.account_role} onValueChange={(v) => handleRole(u.id, v)}>
                  <SelectTrigger className="h-8 w-28" data-testid={`admin-role-select-${u.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r} value={r}>{ROLE_META[r].label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {u.status === 'invited' && (
                  <Button variant="outline" size="sm" title="Resend invitation email" onClick={() => handleResend(u)}
                    className="h-8 border-amber-300 text-amber-700 hover:bg-amber-50"
                    data-testid={`admin-resend-${u.id}`}>
                    <RefreshCw className="h-3.5 w-3.5 mr-1" /> Resend
                  </Button>
                )}
                <Button variant="ghost" size="sm" title={u.status === 'disabled' ? 'Enable' : 'Disable'}
                  className={u.status === 'disabled' ? 'text-green-600' : 'text-gray-500'}
                  onClick={() => handleStatus(u)} data-testid={`admin-toggle-${u.id}`}>
                  <Power className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" title="Remove"
                  onClick={() => handleRemove(u)} data-testid={`admin-remove-${u.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
          {users.length === 0 && (
            <div className="text-center py-8 text-gray-500">No logins for this account yet.</div>
          )}
        </div>
      )}

      <AnnouncementContactsCard
        api={announcementApi}
        testIdPrefix="admin-announcement-contacts"
      />

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Login</DialogTitle>
            <DialogDescription>Add a new login to this client account. They'll receive a set-password invite.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Email *</Label>
              <Input type="email" value={addData.email} data-testid="admin-add-user-email"
                onChange={(e) => setAddData({ ...addData, email: e.target.value })} placeholder="user@company.com" />
            </div>
            <div>
              <Label>Full Name *</Label>
              <Input value={addData.name} data-testid="admin-add-user-name"
                onChange={(e) => setAddData({ ...addData, name: e.target.value })} placeholder="Jane Smith" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={addData.account_role} onValueChange={(v) => setAddData({ ...addData, account_role: v })}>
                <SelectTrigger data-testid="admin-add-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r} data-testid={`admin-add-user-role-${r}`}>{ROLE_META[r].label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={submitting} className="bg-amber-500 hover:bg-amber-600" data-testid="admin-add-user-submit">
              <Mail className="h-4 w-4 mr-2" />{submitting ? 'Sending...' : 'Send Invite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!inviteLink} onOpenChange={(o) => { if (!o) setInviteLink(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Share this setup link</DialogTitle>
            <DialogDescription>
              The invitation email couldn't be delivered. Copy this link and send it to the user so they can set their password (valid 72 hours).
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Input readOnly value={inviteLink || ''} data-testid="admin-invite-link-input" onFocus={(e) => e.target.select()} />
            <Button onClick={() => { navigator.clipboard?.writeText(inviteLink || ''); toast.success('Link copied'); }}
              className="bg-amber-500 hover:bg-amber-600 shrink-0" data-testid="admin-copy-invite-link-btn">
              Copy
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteLink(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersTab;
